import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Check, ChevronDown, ChevronRight, GripVertical, X } from 'lucide-react';
import { Control, Controller, FieldErrors, useWatch } from 'react-hook-form';
import { Button, Divider, Form, Header, Segment } from 'semantic-ui-react';
import { SaveQualityRequirementsPolicyType } from '../../../../../../../schemas/qualityRequirementsPolicy.schemas';
import './bypassTierItem.css';

type BypassTierItemPropsType = {
    id: string;
    control: Control<SaveQualityRequirementsPolicyType>;
    index: number;
    isExpanded: boolean;
    isSubmitting: boolean;
    onToggle: () => void;
    onRemove: () => void;
    errors: FieldErrors<SaveQualityRequirementsPolicyType>;
};

function BypassTierItem({
    id,
    control,
    index,
    isExpanded,
    isSubmitting,
    onToggle,
    onRemove,
    errors,
}: BypassTierItemPropsType) {
    const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id });
    const tier = useWatch({ control, name: `bypassTiers.${index}` });

    const tierErrors = errors.bypassTiers?.[index];

    const headerClass = `bypass-tier-header${tierErrors ? ' bypass-tier-header--error' : ''}`;

    return (
        <div
            ref={setNodeRef}
            style={{ transform: CSS.Transform.toString(transform), transition }}
            className="bypass-tier-item__container"
        >
            <Segment className="bypass-tier-item">
                <div onClick={onToggle} className={headerClass}>
                    <button type="button" className="bypass-tier-header__drag-handle" {...attributes} {...listeners}>
                        <GripVertical size={16} />
                    </button>

                    {isExpanded ? (
                        <ChevronDown className="bypass-tier-header__chevron" size={16} />
                    ) : (
                        <ChevronRight className="bypass-tier-header__chevron" size={16} />
                    )}

                    <strong className="bypass-tier-header__label">Tier {index + 1}</strong>

                    <span className="bypass-tier-header__summary">
                        <span>
                            CD:{tier.requirements.minCDChecks} FC:{tier.requirements.minFullChecks} VC:
                            {tier.requirements.minVisualChecks}
                        </span>
                        <span className="bypass-tier-header__separator">|</span>
                        <span>
                            Mistakes: {tier.maxUpdaterMistakesPer100}/{tier.maxFcReviewerMistakesPer100}/
                            {tier.maxCdReviewerMistakesPer100}/{tier.mistakesIncreasePerReviewerPercent}%
                        </span>
                        <span className="bypass-tier-header__separator">|</span>
                        <span className="bypass-tier-header__flags">
                            {tier.allowOnHistoricalCoreDataChange ? (
                                <Check className="bypass-tier-header__flag-icon--green" size={14} />
                            ) : (
                                <X className="bypass-tier-header__flag-icon--red" size={14} />
                            )}
                            Core
                            {tier.allowOnHistoricalNonDataChange ? (
                                <Check className="bypass-tier-header__flag-icon--green" size={14} />
                            ) : (
                                <X className="bypass-tier-header__flag-icon--red" size={14} />
                            )}
                            Non-Data
                        </span>
                    </span>

                    <Button
                        type="button"
                        size="mini"
                        color="red"
                        icon="trash"
                        className="bypass-tier-header__remove-btn"
                        disabled={isSubmitting}
                        onClick={(e) => {
                            e.stopPropagation();
                            onRemove();
                        }}
                    />
                </div>

                {isExpanded && (
                    <div className="bypass-tier-body">
                        <Divider className="bypass-tier-body__divider" />
                        <Header dividing as="h5">
                            Review Configuration
                        </Header>
                        <Form.Group widths="equal">
                            <Controller
                                name={`bypassTiers.${index}.requirements.minCDChecks`}
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Form.Input
                                        disabled={isSubmitting}
                                        value={value}
                                        onChange={(_, data) => onChange(Number(data.value))}
                                        type="number"
                                        label="Min CD Checks"
                                        error={tierErrors?.requirements?.minCDChecks?.message}
                                    />
                                )}
                            />
                            <Controller
                                name={`bypassTiers.${index}.requirements.minFullChecks`}
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Form.Input
                                        disabled={isSubmitting}
                                        value={value}
                                        onChange={(_, data) => onChange(Number(data.value))}
                                        type="number"
                                        label="Min Full Checks"
                                        error={tierErrors?.requirements?.minFullChecks?.message}
                                    />
                                )}
                            />
                            <Controller
                                name={`bypassTiers.${index}.requirements.minVisualChecks`}
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Form.Input
                                        disabled={isSubmitting}
                                        value={value}
                                        onChange={(_, data) => onChange(Number(data.value))}
                                        type="number"
                                        label="Min Visual Checks"
                                        error={tierErrors?.requirements?.minVisualChecks?.message}
                                    />
                                )}
                            />
                        </Form.Group>

                        {tierErrors?.requirements?.message && (
                            <div className="ui pointing above prompt label">{tierErrors.requirements.message}</div>
                        )}

                        <Divider />
                        <Header dividing as="h5">
                            Bypass Configuration
                        </Header>
                        <Form.Group widths="equal">
                            <Controller
                                name={`bypassTiers.${index}.maxUpdaterMistakesPer100`}
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Form.Input
                                        disabled={isSubmitting}
                                        value={value}
                                        onChange={(_, data) => onChange(Number(data.value))}
                                        type="number"
                                        label="Max Updater Mistakes Per 100"
                                        error={tierErrors?.maxUpdaterMistakesPer100?.message}
                                    />
                                )}
                            />
                            <Controller
                                name={`bypassTiers.${index}.maxFcReviewerMistakesPer100`}
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Form.Input
                                        disabled={isSubmitting}
                                        value={value}
                                        onChange={(_, data) => onChange(Number(data.value))}
                                        type="number"
                                        label="Max FC Reviewer Mistakes Per 100"
                                        error={tierErrors?.maxFcReviewerMistakesPer100?.message}
                                    />
                                )}
                            />
                        </Form.Group>

                        <Form.Group widths="equal">
                            <Controller
                                name={`bypassTiers.${index}.maxCdReviewerMistakesPer100`}
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Form.Input
                                        disabled={isSubmitting}
                                        value={value}
                                        onChange={(_, data) => onChange(Number(data.value))}
                                        type="number"
                                        label="Max CD Reviewer Mistakes Per 100"
                                        error={tierErrors?.maxCdReviewerMistakesPer100?.message}
                                    />
                                )}
                            />
                            <Controller
                                name={`bypassTiers.${index}.mistakesIncreasePerReviewerPercent`}
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Form.Input
                                        disabled={isSubmitting}
                                        value={value}
                                        onChange={(_, data) => onChange(Number(data.value))}
                                        type="number"
                                        label="Mistakes Increase Per Reviewer %"
                                        error={tierErrors?.mistakesIncreasePerReviewerPercent?.message}
                                    />
                                )}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Controller
                                name={`bypassTiers.${index}.allowOnHistoricalCoreDataChange`}
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Form.Checkbox
                                        disabled={isSubmitting}
                                        checked={value}
                                        onChange={(_, data) => onChange(data.checked)}
                                        label="Allow On Historical Core Data Change"
                                    />
                                )}
                            />
                        </Form.Group>

                        <Form.Group>
                            <Controller
                                name={`bypassTiers.${index}.allowOnHistoricalNonDataChange`}
                                control={control}
                                render={({ field: { value, onChange } }) => (
                                    <Form.Checkbox
                                        disabled={isSubmitting}
                                        checked={value}
                                        onChange={(_, data) => onChange(data.checked)}
                                        label="Allow On Historical Non-Data Change"
                                    />
                                )}
                            />
                        </Form.Group>
                    </div>
                )}
            </Segment>
        </div>
    );
}

export default BypassTierItem;
